import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Space,
  Statistic,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  DownloadOutlined,
  EyeOutlined,
  ReloadOutlined,
  RetweetOutlined,
} from '@ant-design/icons';

import {
  downloadAssessmentResults,
  listAssessmentQueue,
  recomputeAssessment,
} from './api';
import { EllipsisText, formatDateTime, QueueStatusTag } from './shared';
import { AssessmentSessionLink } from './types';

type Props = {
  onOpenScenario: (code: string) => void;
};

const OperationsQueue: React.FC<Props> = ({ onOpenScenario }) => {
  const [rows, setRows] = useState<AssessmentSessionLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scenario, setScenario] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [retrying, setRetrying] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(
        await listAssessmentQueue({
          scenario: scenario.trim() || undefined,
          status,
        }),
      );
    } catch (requestError) {
      setError((requestError as Error).message);
    } finally {
      setLoading(false);
    }
  }, [scenario, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const counts = useMemo(
    () =>
      rows.reduce<Record<string, number>>((result, row) => {
        result[row.assessment_status_label] =
          (result[row.assessment_status_label] || 0) + 1;
        return result;
      }, {}),
    [rows],
  );

  const retry = (row: AssessmentSessionLink) => {
    Modal.confirm({
      title: `Retry session ${row.session_id}?`,
      content:
        'This uses the existing recompute endpoint: the same assessment link is marked pending and the existing task is queued. No definition, session link, evidence, or prior result is edited by this management UI.',
      okText: 'Queue retry',
      onOk: async () => {
        setRetrying(row.session_id);
        try {
          await recomputeAssessment(row.session_id);
          message.success(
            `Session ${row.session_id} was queued for recompute.`,
          );
          await load();
        } catch (requestError) {
          message.error((requestError as Error).message);
        } finally {
          setRetrying(null);
        }
      },
    });
  };

  const columns: any[] = [
    { title: 'Session', dataIndex: 'session_id', fixed: 'left', width: 90 },
    {
      title: 'Assessment',
      width: 220,
      render: (_: unknown, row: AssessmentSessionLink) => (
        <Space direction="vertical" size={0}>
          <Tooltip title={row.scenario_name} placement="topLeft">
            <Typography.Link
              className="assessment-ellipsis"
              onClick={() => onOpenScenario(row.scenario)}
            >
              {row.scenario_name}
            </Typography.Link>
          </Tooltip>
          <EllipsisText code value={row.scenario} />
        </Space>
      ),
    },
    {
      title: 'Model',
      dataIndex: 'model_version',
      width: 200,
      render: (value: string) => <EllipsisText code value={value} />,
    },
    {
      title: 'Job state',
      dataIndex: 'assessment_status_label',
      width: 110,
      render: (value: string) => <QueueStatusTag label={value} />,
    },
    { title: 'Session state', dataIndex: 'session_status_label', width: 120 },
    {
      title: 'Class',
      dataIndex: 'klass_name',
      width: 180,
      render: (value: string) => <EllipsisText value={value} />,
    },
    {
      title: 'Trainer',
      dataIndex: 'trainer_name',
      width: 160,
      render: (value: string) => <EllipsisText value={value} />,
    },
    {
      title: 'Blueprint version',
      width: 240,
      render: (_: unknown, row: AssessmentSessionLink) => (
        <EllipsisText
          value={
            row.blueprint
              ? `${row.blueprint.blueprint_name} · v${row.blueprint.version_number} · ${row.blueprint.difficulty}`
              : null
          }
        />
      ),
    },
    { title: 'Results', dataIndex: 'result_count', width: 80 },
    {
      title: 'Computed',
      dataIndex: 'computed_at',
      width: 170,
      className: 'assessment-cell-nowrap',
      render: formatDateTime,
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      width: 170,
      className: 'assessment-cell-nowrap',
      render: formatDateTime,
    },
    {
      title: 'Error',
      dataIndex: 'error',
      width: 260,
      render: (value: string) => <EllipsisText value={value} />,
    },
    {
      title: 'Actions',
      fixed: 'right',
      align: 'right',
      width: 255,
      render: (_: unknown, row: AssessmentSessionLink) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onOpenScenario(row.scenario)}
          >
            Inspect
          </Button>
          <Button
            size="small"
            danger={row.assessment_status_label === 'failed'}
            icon={<RetweetOutlined />}
            loading={retrying === row.session_id}
            disabled={row.assessment_status_label === 'running'}
            onClick={() => retry(row)}
          >
            {row.assessment_status_label === 'failed' ? 'Retry' : 'Recompute'}
          </Button>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            disabled={!row.result_count}
            onClick={() =>
              downloadAssessmentResults(row.session_id, 'csv').catch(
                downloadError => message.error(downloadError.message),
              )
            }
          />
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Assessment operations queue"
      extra={
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Alert
        className="assessment-callout"
        showIcon
        type="info"
        title="Existing runtime job state"
        description="This is a read-only view over assessment session links. Retry and recompute explicitly call the existing endpoint; this surface does not implement a second scheduler or scoring path."
      />
      <div className="assessment-stat-grid">
        {['pending', 'running', 'done', 'failed'].map(label => (
          <Card size="small" key={label}>
            <Statistic title={label.toUpperCase()} value={counts[label] || 0} />
          </Card>
        ))}
      </div>
      <Space className="assessment-filter-row" wrap>
        <Input.Search
          allowClear
          placeholder="Exact scenario code"
          style={{ width: 280 }}
          onSearch={setScenario}
        />
        <Select
          allowClear
          placeholder="All job states"
          style={{ width: 180 }}
          value={status}
          onChange={setStatus}
          options={['pending', 'running', 'done', 'failed'].map(value => ({
            value,
            label: value.toUpperCase(),
          }))}
        />
      </Space>
      {error && (
        <Alert
          className="assessment-callout"
          showIcon
          type="error"
          title={error}
        />
      )}
      <Table
        rowKey="link_id"
        className="assessment-table"
        tableLayout="fixed"
        loading={loading}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 2255 }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </Card>
  );
};

export default OperationsQueue;
